import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle, User, MapPin, FileText, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  { id: 0, label: "Person KYC", icon: User, desc: "Verify your identity" },
  { id: 1, label: "Property Location", icon: MapPin, desc: "Survey records" },
  { id: 2, label: "Property Details", icon: FileText, desc: "Additional info" },
  { id: 3, label: "Confirmation", icon: Shield, desc: "Review & submit" },
];

export default function PropertyOnboarding() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [kycDone, setKycDone] = useState(false);
  const [loading, setLoading] = useState(false);

  // KYC form
  const [kyc, setKyc] = useState({ full_name: "", aadhaar_last4: "", pan_number: "", phone: "", address_line: "", city: "", state: "Goa", pincode: "" });

  // Location cascading
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);

  // Property form
  const [prop, setProp] = useState({
    district: "", taluka: "", village: "", survey_number: "", sub_division: "",
    name: "", property_type: "settlement", land_type: "settlement", area_sqft: "", valuation: "", notes: ""
  });

  // Check existing KYC
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/kyc/person`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (data.status === "verified") {
            setKycDone(true);
            setKyc(data);
            setStep(1);
          }
        }
      } catch (e) { console.error(e); }
    })();
  }, [API]);

  // Fetch districts
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/locations/districts`, { credentials: "include" });
        if (res.ok) setDistricts(await res.json());
      } catch (e) { console.error(e); }
    })();
  }, [API]);

  // Fetch talukas when district changes
  useEffect(() => {
    if (!prop.district) { setTalukas([]); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/locations/talukas?district=${encodeURIComponent(prop.district)}`, { credentials: "include" });
        if (res.ok) setTalukas(await res.json());
      } catch (e) { console.error(e); }
    })();
  }, [API, prop.district]);

  // Fetch villages when taluka changes
  useEffect(() => {
    if (!prop.district || !prop.taluka) { setVillages([]); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/locations/villages?district=${encodeURIComponent(prop.district)}&taluka=${encodeURIComponent(prop.taluka)}`, { credentials: "include" });
        if (res.ok) setVillages(await res.json());
      } catch (e) { console.error(e); }
    })();
  }, [API, prop.district, prop.taluka]);

  const submitKyc = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/kyc/person`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(kyc),
      });
      if (res.ok) {
        setKycDone(true);
        setStep(1);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submitProperty = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/properties/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...prop,
          valuation: parseFloat(prop.valuation) || 0,
          area_sqft: parseFloat(prop.area_sqft) || 0,
        }),
      });
      if (res.ok) {
        navigate("/dashboard/properties");
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const canProceed = () => {
    if (step === 0) return kyc.full_name && kyc.aadhaar_last4 && kyc.pan_number && kyc.phone;
    if (step === 1) return prop.district && prop.taluka && prop.village && prop.survey_number;
    if (step === 2) return true;
    return true;
  };

  return (
    <div data-testid="property-onboarding-page" className="max-w-3xl mx-auto">
      <div className="mb-8">
        <h2 className="font-serif text-2xl text-white">Onboard Property</h2>
        <p className="text-white/40 text-sm font-sans">Two-step KYC: Verify yourself, then register your property via survey records</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center flex-1">
            <div className={`flex items-center gap-2 ${i <= step ? "text-[#D4AF37]" : "text-white/20"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-sans font-bold border ${
                i < step ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                : i === step ? "border-[#D4AF37] text-[#D4AF37]"
                : "border-white/10 text-white/20"
              }`}>
                {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-sans font-medium">{s.label}</p>
                <p className="text-[10px] text-white/30 font-sans">{s.desc}</p>
              </div>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-[#D4AF37]" : "bg-white/10"}`} />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-[#11130F] border border-white/10 rounded-sm p-6">
        {/* Step 0: Person KYC */}
        {step === 0 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-serif text-lg text-white">Person KYC</h3>
              {kycDone && <span className="bg-[#556B2F]/20 text-[#556B2F] text-[10px] px-2 py-0.5 rounded-sm font-sans">Verified</span>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Full Legal Name *</label>
                <Input data-testid="kyc-name" value={kyc.full_name} onChange={e => setKyc({...kyc, full_name: e.target.value})} placeholder="As per Aadhaar" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Phone Number *</label>
                <Input data-testid="kyc-phone" value={kyc.phone} onChange={e => setKyc({...kyc, phone: e.target.value})} placeholder="+91 XXXXX XXXXX" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Aadhaar (Last 4 digits) *</label>
                <Input data-testid="kyc-aadhaar" value={kyc.aadhaar_last4} onChange={e => setKyc({...kyc, aadhaar_last4: e.target.value.slice(0,4)})} placeholder="XXXX" maxLength={4} className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">PAN Number *</label>
                <Input data-testid="kyc-pan" value={kyc.pan_number} onChange={e => setKyc({...kyc, pan_number: e.target.value.toUpperCase().slice(0,10)})} placeholder="ABCDE1234F" maxLength={10} className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-white/50 text-xs font-sans mb-1 block">Residential Address</label>
                <Input value={kyc.address_line} onChange={e => setKyc({...kyc, address_line: e.target.value})} placeholder="Street address" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">City</label>
                <Input value={kyc.city} onChange={e => setKyc({...kyc, city: e.target.value})} placeholder="City" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Pincode</label>
                <Input value={kyc.pincode} onChange={e => setKyc({...kyc, pincode: e.target.value.slice(0,6)})} placeholder="403001" maxLength={6} className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Property Location (Survey Records) */}
        {step === 1 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-serif text-lg text-white">Property Survey Records</h3>
            </div>
            <p className="text-white/40 text-xs font-sans mb-4">Identify your property using official Goa survey records: District → Taluka → Village → Survey Number</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">District *</label>
                <Select value={prop.district} onValueChange={v => setProp({...prop, district: v, taluka: "", village: ""})}>
                  <SelectTrigger data-testid="onboard-district" className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select District" /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    {districts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Taluka *</label>
                <Select value={prop.taluka} onValueChange={v => setProp({...prop, taluka: v, village: ""})} disabled={!prop.district}>
                  <SelectTrigger data-testid="onboard-taluka" className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select Taluka" /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    {talukas.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Village *</label>
                <Select value={prop.village} onValueChange={v => setProp({...prop, village: v})} disabled={!prop.taluka}>
                  <SelectTrigger data-testid="onboard-village" className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select Village" /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    {villages.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Land Type</label>
                <Select value={prop.land_type} onValueChange={v => setProp({...prop, land_type: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    <SelectItem value="settlement">Settlement</SelectItem>
                    <SelectItem value="agriculture">Agriculture</SelectItem>
                    <SelectItem value="mixed">Mixed Use</SelectItem>
                    <SelectItem value="communidade">Communidade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Survey Number *</label>
                <Input data-testid="onboard-survey" value={prop.survey_number} onChange={e => setProp({...prop, survey_number: e.target.value})} placeholder="e.g., 123" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Sub-Division</label>
                <Input data-testid="onboard-subdivision" value={prop.sub_division} onChange={e => setProp({...prop, sub_division: e.target.value})} placeholder="e.g., 1A" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Additional Details */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-serif text-lg text-white">Property Details</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-white/50 text-xs font-sans mb-1 block">Property Name (optional)</label>
                <Input data-testid="onboard-name" value={prop.name} onChange={e => setProp({...prop, name: e.target.value})} placeholder={`Survey ${prop.survey_number}${prop.sub_division ? '/' + prop.sub_division : ''}`} className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Property Type</label>
                <Select value={prop.property_type} onValueChange={v => setProp({...prop, property_type: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    <SelectItem value="settlement">Residential/Settlement</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="agricultural">Agricultural</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Area (sq ft)</label>
                <Input type="number" value={prop.area_sqft} onChange={e => setProp({...prop, area_sqft: e.target.value})} placeholder="0" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Estimated Valuation (INR)</label>
                <Input type="number" value={prop.valuation} onChange={e => setProp({...prop, valuation: e.target.value})} placeholder="0" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Notes</label>
                <Input value={prop.notes} onChange={e => setProp({...prop, notes: e.target.value})} placeholder="Any additional details..." className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-serif text-lg text-white">Review & Confirm</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">Person KYC</p>
                <div className="space-y-1.5 text-sm font-sans">
                  <div className="flex justify-between"><span className="text-white/40">Name</span><span className="text-white">{kyc.full_name}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Aadhaar</span><span className="text-white">XXXX-XXXX-{kyc.aadhaar_last4}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">PAN</span><span className="text-white">{kyc.pan_number}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Phone</span><span className="text-white">{kyc.phone}</span></div>
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">Property Survey</p>
                <div className="space-y-1.5 text-sm font-sans">
                  <div className="flex justify-between"><span className="text-white/40">District</span><span className="text-white">{prop.district}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Taluka</span><span className="text-white">{prop.taluka}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Village</span><span className="text-white">{prop.village}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Survey No.</span><span className="text-white">{prop.survey_number}{prop.sub_division ? `/${prop.sub_division}` : ""}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Type</span><span className="text-white capitalize">{prop.land_type}</span></div>
                </div>
              </div>
            </div>
            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-sm p-4">
              <p className="text-white/60 text-xs font-sans leading-relaxed">
                By confirming, you declare that you are the rightful owner or authorized representative for the above property. The property will be onboarded with <strong className="text-[#D4AF37]">KYC Complete</strong> badge and begin at <strong className="text-[#D4AF37]">Lifecycle State 1 (Claimed)</strong>.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
          <Button
            data-testid="onboard-back-btn"
            onClick={() => setStep(Math.max(kycDone ? 1 : 0, step - 1))}
            disabled={step === 0 || (step === 1 && !kycDone)}
            variant="outline"
            className="border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          {step === 0 && !kycDone && (
            <Button data-testid="onboard-submit-kyc" onClick={submitKyc} disabled={!canProceed() || loading} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">
              {loading ? "Verifying..." : "Verify Identity"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          {step > 0 && step < 3 && (
            <Button data-testid="onboard-next-btn" onClick={() => setStep(step + 1)} disabled={!canProceed()} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          {step === 3 && (
            <Button data-testid="onboard-confirm-btn" onClick={submitProperty} disabled={loading} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">
              {loading ? "Onboarding..." : "Confirm & Onboard Property"} <CheckCircle className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
